import { useState } from "react";
import { Card } from "../../../ui/card";
import { Badge } from "../../../ui/badge";
import { Interview } from "../../../../types/interview";
import { format } from "date-fns";
import { Calendar, Clock, User, Building2, CheckCircle2, XCircle, ChevronRight } from "lucide-react";
import { Button } from "../../../ui/button";

interface CompanyGroupedInterviewsProps {
  interviews: Interview[];
  filter: "all" | "completed" | "pending";
}

export const CompanyGroupedInterviews = ({ interviews, filter }: CompanyGroupedInterviewsProps) => {
  const [selectedCompany, setSelectedCompany] = useState<string | null>(null);

  const filteredInterviews = interviews.filter((interview) => {
    if (filter === "completed") return interview.isCompleted;
    if (filter === "pending") return !interview.isCompleted;
    return true;
  });

  // Group interviews by company
  const groupedByCompany = filteredInterviews.reduce((acc, interview) => {
    const company = interview.companyName || "Unknown";
    if (!acc[company]) {
      acc[company] = [];
    }
    acc[company].push(interview);
    return acc;
  }, {} as Record<string, Interview[]>);

  const companies = Object.entries(groupedByCompany).sort(
    ([, a], [, b]) => b.length - a.length
  );

  const handleCompanyClick = (company: string) => {
    setSelectedCompany(selectedCompany === company ? null : company);
  };

  const handleBackToAll = () => {
    setSelectedCompany(null);
  };

  // If a company is selected, show only that company's interviews
  if (selectedCompany) {
    const companyInterviews = groupedByCompany[selectedCompany] || [];
    return (
      <div className="space-y-4">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-3">
            <Button 
              variant="ghost" 
              size="sm"
              onClick={handleBackToAll}
              className="text-primary hover:text-primary/80"
            >
              ← Back to All Companies
            </Button>
            <h3 className="text-xl font-semibold text-card-foreground">
              {selectedCompany}
            </h3>
          </div>
          <span className="text-sm text-muted-foreground">
            {companyInterviews.length} interviews
          </span>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {companyInterviews.map((interview) => (
            <Card
              key={interview.id}
              className="p-5 shadow-md border-0 hover:shadow-xl transition-all duration-300 hover:-translate-y-1 animate-scale-in"
              style={{ borderLeft: `4px solid ${interview.color}` }}
            >
              <div className="space-y-3">
                <div className="flex items-start justify-between">
                  <h4 className="font-semibold text-card-foreground line-clamp-1">
                    {interview.roundDetails}
                  </h4>
                  {interview.isCompleted ? (
                    <Badge variant="outline" className="bg-success/10 text-success border-success">
                      <CheckCircle2 className="w-3 h-3 mr-1" />
                      Done
                    </Badge>
                  ) : (
                    <Badge variant="outline" className="bg-warning/10 text-warning border-warning">
                      <XCircle className="w-3 h-3 mr-1" />
                      Pending
                    </Badge>
                  )}
                </div>

                <div className="space-y-2 text-sm">
                  <div className="flex items-center gap-2 text-muted-foreground">
                    <User className="w-4 h-4 text-primary" />
                    <span className="truncate">{interview.candidateName}</span>
                  </div>
                  <div className="flex items-center gap-2 text-muted-foreground">
                    <Calendar className="w-4 h-4 text-primary" />
                    <span>{format(new Date(interview.date), "MMM dd, yyyy")}</span>
                  </div>
                  <div className="flex items-center gap-2 text-muted-foreground">
                    <Clock className="w-4 h-4 text-primary" />
                    <span>
                      {format(new Date(interview.start), "hh:mm a")} - {format(new Date(interview.end), "hh:mm a")}
                    </span>
                  </div>
                </div>

                {interview.panelName && (
                  <div className="pt-3 border-t border-border">
                    <p className="text-xs text-muted-foreground">
                      Panel: <span className="font-medium text-foreground">{interview.panelName}</span>
                    </p>
                  </div>
                )}
              </div>
            </Card>
          ))}
        </div>
      </div>
    );
  }

  // Show company groups
  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-xl font-semibold text-card-foreground">
          Companies
        </h3>
        <span className="text-sm text-muted-foreground">
          {companies.length} companies
        </span>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {companies.map(([company, companyInterviews]) => {
          const completed = companyInterviews.filter((i) => i.isCompleted).length;
          const pending = companyInterviews.length - completed;
          
          return (
            <Card
              key={company}
              className="p-5 shadow-md border-0 hover:shadow-xl transition-all duration-300 hover:-translate-y-1 cursor-pointer animate-scale-in group"
              onClick={() => handleCompanyClick(company)}
            >
              <div className="space-y-3">
                <div className="flex items-start justify-between">
                  <div className="flex items-center gap-2">
                    <Building2 className="w-5 h-5 text-primary" />
                    <h4 className="font-semibold text-card-foreground">
                      {company}
                    </h4>
                  </div>
                  <ChevronRight className="w-5 h-5 text-muted-foreground group-hover:text-primary transition-colors" />
                </div>

                <div className="flex items-center gap-4 text-sm">
                  <div className="flex items-center gap-1">
                    <div className="w-2 h-2 rounded-full bg-primary" />
                    <span className="font-medium text-foreground">{companyInterviews.length}</span>
                    <span className="text-muted-foreground">total</span>
                  </div>
                </div>

                <div className="flex items-center gap-3 pt-2 border-t border-border">
                  <div className="flex items-center gap-1 text-xs">
                    <CheckCircle2 className="w-3 h-3 text-success" />
                    <span className="text-muted-foreground">{completed} completed</span>
                  </div>
                  <div className="flex items-center gap-1 text-xs">
                    <XCircle className="w-3 h-3 text-warning" />
                    <span className="text-muted-foreground">{pending} pending</span>
                  </div>
                </div>
              </div>
            </Card>
          );
        })}
      </div>
    </div>
  );
};
